import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../../components/Table/Table";
import {
  getAccounts,
  deleteAccount,
  patchAccount,
} from "../../../services/auth/accountService";
import AccountPageHeader from "./PageHeader";
import AccountFilters from "./Filters";
import AccountForm from "./AccountForm";
import { useAccountColumns } from "./columns";
import { toast } from "react-toastify";
import { Button } from "../../../components/forms/Buttons/Button";

export default function Accounts() {
  const [t] = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const PAGE_SIZE = 50;

  // Filters
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    email: "",
    username: "",
    phone_number: "",
    gender: "",
    grade: null,
  });

  const fetchData = () => {
    setLoading(true);
    getAccounts({
      page: page,
      page_size: PAGE_SIZE,
      name: nameFilter || undefined,
      email: emailFilter || undefined,
    })
      .then((res) => {
        setData(res.results || []);
        setCount(res.count || 0);
        setPageCount(Math.ceil((res.count || 0) / PAGE_SIZE));
      })
      .catch((err) => {
        console.error("Error fetching accounts:", err);
        toast.error(t("admin.error_fetching_data"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setPage(1);
  }, [nameFilter, emailFilter]);

  useEffect(() => {
    fetchData();
  }, [page, nameFilter, emailFilter]);

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      first_name: account.first_name || "",
      last_name: account.last_name || "",
      middle_name: account.middle_name || "",
      email: account.email || "",
      username: account.username || "",
      phone_number: account.phone_number?.replace(/^\+7/, "") || "",
      gender: account.gender || "",
      grade: account.grade || null,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = (data) => {
    patchAccount(editingAccount.uuid, data)
      .then(() => {
        toast.success(t("admin.account_updated"));
        setIsEditModalOpen(false);
        setEditingAccount(null);
        setFormData({
          first_name: "",
          last_name: "",
          middle_name: "",
          email: "",
          username: "",
          phone_number: "",
          gender: "",
          grade: null,
        });
        fetchData();
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail || t("admin.error_updating_account")
        );
      });
  };

  const handleDelete = (account) => {
    if (toast.isActive(account.uuid)) return;
    toast(
      <div className="flex flex-col gap-2 pt-2">
        <p>
          {t("admin.confirm_delete_account", {
            account: account.username || account.email || account.first_name,
          })}
        </p>
        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.delete")}
            color="error"
            onClick={() => {
              toast.dismiss(account.uuid);
              deleteAccount(account.uuid)
                .then(() => {
                  toast.success(t("admin.account_deleted"));
                  fetchData();
                })
                .catch((err) => {
                  toast.error(
                    err.response?.data?.detail ||
                      t("admin.error_deleting_account")
                  );
                });
            }}
          />
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            onClick={() => toast.dismiss(account.uuid)}
          />
        </div>
      </div>,
      { hideProgressBar: true, toastId: account.uuid }
    );
  };

  const columns = useAccountColumns(page, PAGE_SIZE, handleEdit, handleDelete);

  return (
    <div className="p-6">
      <AccountPageHeader />

      <AccountFilters
        nameFilter={nameFilter}
        setNameFilter={setNameFilter}
        emailFilter={emailFilter}
        setEmailFilter={setEmailFilter}
      />

      <Table
        columns={columns}
        data={data}
        loading={loading}
        count={count}
        pageNumber={page}
        pageCount={pageCount}
        nextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
        previousPage={() => setPage((p) => Math.max(p - 1, 1))}
        gotoPage={(p) => setPage(p)}
      />

      <AccountForm
        isOpen={isEditModalOpen}
        isEdit={true}
        formData={formData}
        setFormData={setFormData}
        accountRoles={editingAccount?.roles || []}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingAccount(null);
          setFormData({
            first_name: "",
            last_name: "",
            middle_name: "",
            email: "",
            username: "",
            phone_number: "",
            gender: "",
            grade: null,
          });
        }}
        onSubmit={(data) => handleUpdate(data)}
      />
    </div>
  );
}
